import { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Camera, Upload, X, Loader2, Image as ImageIcon, Plus, ChevronLeft, ChevronRight } from 'lucide-react';

// Image compression utility
const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        canvas.toBlob(
          (blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          quality
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

export const MultiImageUpload = ({ 
  onImagesChange, 
  maxImages = 5,
  initialImages = [],
  className = ''
}) => {
  const { api } = useAuth();
  const [images, setImages] = useState(initialImages);
  const [uploading, setUploading] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Check max images limit
    const remainingSlots = maxImages - images.length;
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${maxImages} images allowed`);
      return;
    }

    const filesToUpload = files.slice(0, remainingSlots);
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    const invalidFiles = filesToUpload.filter(f => !validTypes.includes(f.type));
    if (invalidFiles.length) {
      toast.error('Please select JPEG, PNG, WebP, or GIF images only');
      return;
    }

    setUploading(true);
    
    try {
      const uploadedUrls = [];
      
      for (const file of filesToUpload) {
        // Compress if larger than 500KB
        let fileToUpload = file;
        if (file.size > 500 * 1024) {
          fileToUpload = await compressImage(file, 1200, 0.85);
        }
        
        const formData = new FormData();
        formData.append('file', fileToUpload);

        const response = await api().post('/upload/listing-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        // Store only the relative path - will be resolved at display time
        const imageUrl = response.data.image_url;
        uploadedUrls.push(imageUrl);
      }
      
      const newImages = [...images, ...uploadedUrls];
      setImages(newImages);
      setCurrentIndex(newImages.length - 1);
      
      if (onImagesChange) {
        onImagesChange(newImages);
      }
      
      toast.success(`${uploadedUrls.length} image${uploadedUrls.length > 1 ? 's' : ''} uploaded`);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload images');
    } finally {
      setUploading(false);
      // Reset input
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    
    // Adjust current index if needed
    if (currentIndex >= newImages.length) {
      setCurrentIndex(Math.max(0, newImages.length - 1));
    }
    
    if (onImagesChange) {
      onImagesChange(newImages);
    }
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Main Image Display */}
      <div className="relative aspect-[4/3] bg-zinc-900 rounded-xl overflow-hidden border border-white/10">
        {images.length > 0 ? (
          <>
            <img 
              src={images[currentIndex]} 
              alt={`Upload ${currentIndex + 1}`}
              className="w-full h-full object-cover"
            />
            
            {/* Navigation Arrows */}
            {images.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                  data-testid="prev-image"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <button
                  onClick={goToNext}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/60 rounded-full hover:bg-black/80 transition-colors"
                  data-testid="next-image"
                >
                  <ChevronRight className="w-5 h-5 text-white" />
                </button>
              </>
            )}
            
            {/* Image Counter */}
            <div className="absolute top-3 right-3 px-3 py-1 bg-black/70 rounded-full text-sm text-white">
              {currentIndex + 1} / {images.length}
            </div>
            
            {/* Remove Button */}
            <button
              onClick={() => removeImage(currentIndex)}
              className="absolute top-3 left-3 p-2 bg-red-600/80 rounded-full hover:bg-red-600 transition-colors"
              data-testid="remove-current-image"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-8">
            <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center">
              <ImageIcon className="w-8 h-8 text-blue-400" />
            </div>
            <div className="text-center">
              <p className="text-white font-medium mb-1">Add Photos</p>
              <p className="text-zinc-500 text-sm">Up to {maxImages} images • JPEG, PNG, WebP</p>
            </div>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="border-zinc-600 hover:border-blue-500 hover:bg-blue-500/10"
              >
                <Upload className="w-4 h-4 mr-2" />
                Gallery
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploading}
                className="bg-blue-600 hover:bg-blue-500"
              >
                <Camera className="w-4 h-4 mr-2" />
                Camera
              </Button>
            </div>
          </div>
        )}
        
        {uploading && (
          <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center gap-3">
            <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
            <span className="text-white font-medium">Uploading...</span>
          </div>
        )}
      </div>

      {/* Thumbnail Strip */}
      {images.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                index === currentIndex 
                  ? 'border-blue-500 ring-2 ring-blue-500/30' 
                  : 'border-white/10 hover:border-white/30'
              }`}
              data-testid={`thumbnail-${index}`}
            >
              <img src={img} alt={`Thumbnail ${index + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
          
          {/* Add More Button */}
          {images.length < maxImages && (
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex-shrink-0 w-16 h-16 rounded-lg border-2 border-dashed border-zinc-600 hover:border-blue-500 flex items-center justify-center transition-colors"
              data-testid="add-more-images"
            >
              <Plus className="w-6 h-6 text-zinc-500" />
            </button>
          )}
        </div>
      )}

      {/* Add Photos Button (when images exist) */}
      {images.length > 0 && images.length < maxImages && (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1 border-zinc-700 hover:border-blue-500"
          >
            <Upload className="w-4 h-4 mr-2" />
            Add from Gallery ({maxImages - images.length} remaining)
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={() => cameraInputRef.current?.click()}
            disabled={uploading}
            className="bg-blue-600 hover:bg-blue-500"
          >
            <Camera className="w-4 h-4 mr-2" />
            Camera
          </Button>
        </div>
      )}

      {/* Hidden File Inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        data-testid="multi-gallery-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileSelect}
        className="hidden"
        data-testid="multi-camera-input"
      />
    </div>
  );
};

export default MultiImageUpload;
